import { useState, useEffect } from 'react';
import { Users, CheckCircle2, Phone, Eye, XCircle, Loader2, Clock, MapPin, Star, ExternalLink, Copy, ChevronDown, RefreshCw, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

const STATUS_BADGE = {
  new: { label: 'New', cls: 'bg-blue-100 text-blue-700' },
  reviewed: { label: 'Reviewed', cls: 'bg-amber-100 text-amber-700' },
  providers_matched: { label: 'Providers matched', cls: 'bg-amber-100 text-amber-700' },
  matched: { label: 'Matched', cls: 'bg-purple-100 text-purple-700' },
  contacted: { label: 'Contacted', cls: 'bg-green-100 text-green-700' },
  closed: { label: 'Closed', cls: 'bg-muted text-muted-foreground' },
};

function leadAge(requestedAt) {
  if (!requestedAt) return { text: '—', urgent: false };
  const hours = Math.floor((Date.now() - new Date(requestedAt).getTime()) / 3600000);
  if (hours < 1) return { text: 'Just now', urgent: false };
  if (hours < 24) return { text: `${hours}h ago`, urgent: false };
  const days = Math.floor(hours / 24);
  if (days === 1) return { text: '1 day ago', urgent: true };
  return { text: `${days} days ago`, urgent: days >= 2 };
}

function copyText(text) {
  navigator.clipboard.writeText(text).then(() => toast.success('Copied')).catch(() => {});
}

function InstallerMatchCard({ installer, onSelect }) {
  return (
    <div className="flex items-center justify-between gap-2 bg-white border border-border rounded-lg px-3 py-2">
      <div className="min-w-0">
        <p className="text-xs font-medium">{installer.company_name}</p>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
          <MapPin className="w-3 h-3 flex-shrink-0" />
          <span>{installer.city}, {installer.region}</span>
          {installer.region_match && <span className="text-green-600 font-medium">In region</span>}
          {installer.review_score && (
            <span className="flex items-center gap-0.5 text-amber-600"><Star className="w-3 h-3 fill-amber-400" />{installer.review_score}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {installer.phone && (
          <button onClick={() => copyText(installer.phone)} className="text-[10px] text-muted-foreground hover:text-primary" title="Copy phone">
            <Phone className="w-3 h-3" />
          </button>
        )}
        {installer.website && (
          <a href={installer.website} target="_blank" rel="noopener noreferrer" className="text-[10px] text-muted-foreground hover:text-primary" title="Website">
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <button onClick={() => onSelect(installer)} className="px-2 py-1 text-[10px] bg-purple-600 text-white rounded hover:bg-purple-700">
          Select
        </button>
      </div>
    </div>
  );
}

function MatchPanel({ lead, token, onMatched }) {
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const findMatches = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads/${lead.request_id}/match`, {
        headers: { Cookie: `access_token=${token}` }, withCredentials: true,
      });
      setMatches(data);
      setSearched(true);
    } catch { toast.error('Match search failed'); }
    setLoading(false);
  };

  const handleSelect = async (installer) => {
    try {
      await axios.put(`${API}/admin/leads/${lead.request_id}`, {
        status: 'matched',
        matched_installers: [...(lead.matched_installers || []), {
          installer_id: installer.installer_id,
          company_name: installer.company_name,
          city: installer.city,
          region: installer.region,
          phone: installer.phone || '',
          website: installer.website || '',
        }],
        admin_notes: `Matched with ${installer.company_name} (${installer.city})`,
      }, { headers: { Cookie: `access_token=${token}` }, withCredentials: true });
      toast.success(`Matched with ${installer.company_name}`);
      onMatched();
    } catch { toast.error('Match failed'); }
  };

  return (
    <div className="border-t border-purple-200 bg-purple-50/30 p-3 space-y-2" data-testid={`match-panel-${lead.request_id}`}>
      <div className="flex items-center justify-between">
        <h5 className="text-xs font-semibold text-purple-700">Find Matching Installers</h5>
        <button onClick={findMatches} disabled={loading}
          className="px-3 py-1 text-[10px] bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
          data-testid={`find-matches-${lead.request_id}`}>
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Users className="w-3 h-3" />}
          {searched ? 'Search Again' : 'Find Matches'}
        </button>
      </div>

      {matches && (
        <>
          <p className="text-[10px] text-muted-foreground">
            Searching: {matches.search?.specialization} in {matches.search?.region || 'all regions'} · {matches.total_found} found
          </p>
          {matches.matches.length > 0 ? (
            <div className="space-y-1.5">
              {matches.matches.map(ins => (
                <InstallerMatchCard key={ins.installer_id} installer={ins} onSelect={handleSelect} />
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No matching installers found for this region and specialization.</p>
          )}
        </>
      )}

      {/* Show already matched installers */}
      {lead.matched_installers?.length > 0 && (
        <div className="pt-2 border-t border-purple-200">
          <p className="text-[10px] font-semibold text-purple-700 mb-1">Matched Installers:</p>
          {lead.matched_installers.map((ins, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-purple-700 bg-white rounded px-2 py-1 mb-1">
              <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
              <span className="font-medium">{ins.company_name}</span>
              <span className="text-muted-foreground">{ins.city}</span>
              {ins.phone && (
                <button onClick={() => copyText(ins.phone)} className="text-[10px] text-primary hover:underline flex items-center gap-0.5 ml-auto">
                  <Copy className="w-3 h-3" /> {ins.phone}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProviderMatchPanel({ lead, token, onChanged }) {
  const [running, setRunning] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [providers, setProviders] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingProv, setLoadingProv] = useState(false);
  const [savingOv, setSavingOv] = useState(false);

  const opts = { headers: { Cookie: `access_token=${token}` }, withCredentials: true };

  const runMatch = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/leads/${lead.request_id}/match-providers`, {}, opts);
      toast.success(`Auto-matched ${data.matched.length} provider(s)`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Match failed');
    }
    setRunning(false);
  };

  const openOverride = async () => {
    setShowOverride(true);
    setLoadingProv(true);
    try {
      const { data } = await axios.get(`${API}/admin/providers`, opts);
      const filtered = (data.providers || []).filter(
        (p) => p.status === 'active' && (p.categories || []).includes(lead.category)
      );
      setProviders(filtered);
      setSelected((lead.matched_providers || []).map((m) => m.provider_id));
    } catch {
      toast.error('Failed to load providers');
    }
    setLoadingProv(false);
  };

  const toggle = (pid) => {
    setSelected((s) => {
      if (s.includes(pid)) return s.filter((x) => x !== pid);
      if (s.length >= 3) {
        toast.error('Maximum 3 providers');
        return s;
      }
      return [...s, pid];
    });
  };

  const saveOverride = async () => {
    setSavingOv(true);
    try {
      await axios.post(
        `${API}/admin/leads/${lead.request_id}/override-providers`,
        { provider_ids: selected },
        opts
      );
      toast.success('Providers overridden');
      setShowOverride(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Override failed');
    }
    setSavingOv(false);
  };

  const matched = lead.matched_providers || [];

  return (
    <div className="border-t border-amber-200 bg-amber-50/30 p-3 space-y-2" data-testid={`provider-panel-${lead.request_id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h5 className="text-xs font-semibold text-amber-700">Matched Providers (up to 3)</h5>
        <div className="flex gap-2">
          <button
            onClick={runMatch}
            disabled={running}
            className="px-3 py-1 text-[10px] bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1"
            data-testid={`match-providers-${lead.request_id}`}
          >
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {matched.length > 0 ? 'Re-match' : 'Match 3 providers'}
          </button>
          <button
            onClick={openOverride}
            className="px-3 py-1 text-[10px] bg-white border border-amber-600 text-amber-700 rounded hover:bg-amber-50 flex items-center gap-1"
            data-testid={`override-providers-${lead.request_id}`}
          >
            <Pencil className="w-3 h-3" /> Override
          </button>
        </div>
      </div>

      {matched.length > 0 ? (
        <div className="space-y-1">
          {matched.map((m, i) => (
            <div key={m.provider_id || i} className="flex items-center gap-2 text-xs bg-white rounded px-2 py-1.5 border border-amber-200">
              <CheckCircle2 className="w-3 h-3 text-amber-600 flex-shrink-0" />
              <span className="font-medium">{m.company}</span>
              <span className="text-muted-foreground">· {m.provider_name}</span>
              <span className="text-muted-foreground ml-auto text-[10px]">{m.email}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-muted-foreground">No providers matched yet. Click "Match 3 providers" to run auto-matching.</p>
      )}

      {/* Override modal */}
      {showOverride && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setShowOverride(false)}
        >
          <div
            className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="provider-override-modal"
          >
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Override providers</h3>
                <p className="text-[11px] text-muted-foreground">
                  Select up to 3 providers for this lead · Category: {lead.category?.replace(/_/g, ' ')}
                </p>
              </div>
              <button onClick={() => setShowOverride(false)} className="text-muted-foreground hover:text-foreground">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-3 space-y-1">
              {loadingProv ? (
                <p className="text-xs text-muted-foreground p-4 text-center">Loading…</p>
              ) : providers.length === 0 ? (
                <p className="text-xs text-muted-foreground p-4 text-center">
                  No active providers support category "{lead.category}". Add one in the Providers tab.
                </p>
              ) : (
                providers.map((p) => {
                  const isSelected = selected.includes(p.provider_id);
                  return (
                    <label
                      key={p.provider_id}
                      className={`flex items-start gap-2 p-2 rounded cursor-pointer border ${isSelected ? 'bg-primary/5 border-primary' : 'border-border hover:bg-muted/40'}`}
                      data-testid={`override-provider-row-${p.provider_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggle(p.provider_id)}
                        className="mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium">{p.company}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {p.name} · {p.email} · Prio {p.priority ?? 5}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {(p.service_areas || []).length > 0 || (p.service_cities || []).length > 0
                            ? `${(p.service_areas || []).join(', ')} ${(p.service_cities || []).length ? '· ' + (p.service_cities || []).join(', ') : ''}`
                            : 'Nationwide'}
                        </p>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
            <div className="p-3 border-t border-border flex justify-between items-center">
              <p className="text-[11px] text-muted-foreground">{selected.length}/3 selected</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowOverride(false)}
                  className="px-3 py-1.5 text-xs bg-muted text-foreground rounded hover:bg-muted/80"
                >
                  Cancel
                </button>
                <button
                  onClick={saveOverride}
                  disabled={savingOv}
                  className="px-3 py-1.5 text-xs bg-primary text-white rounded hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
                  data-testid="save-override-btn"
                >
                  {savingOv ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LeadsTab({ token }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [notes, setNotes] = useState('');
  const [updating, setUpdating] = useState(null);
  const [sortOldest, setSortOldest] = useState(true);

  const opts = { headers: { Cookie: `access_token=${token}` }, withCredentials: true };

  const loadLeads = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/leads`;
      const params = [];
      if (statusFilter) params.push(`status=${statusFilter}`);
      if (catFilter) params.push(`category=${catFilter}`);
      if (params.length) url += `?${params.join('&')}`;
      const { data } = await axios.get(url, opts);
      setLeads(data.leads || []);
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { if (token) loadLeads(); }, [token, statusFilter, catFilter]);

  const updateLead = async (requestId, status, extraFields = {}) => {
    setUpdating(requestId);
    try {
      await axios.put(`${API}/admin/leads/${requestId}`, {
        status,
        admin_notes: notes || undefined,
        ...extraFields,
      }, opts);
      toast.success(`Lead → ${status}`);
      await loadLeads();
      setNotes('');
    } catch { toast.error('Update failed'); }
    setUpdating(null);
  };

  const statusCounts = {};
  for (const l of leads) {
    statusCounts[l.request_status || 'new'] = (statusCounts[l.request_status || 'new'] || 0) + 1;
  }

  // Sort: oldest first for action urgency, or newest first
  const sorted = [...leads].sort((a, b) => {
    if (sortOldest) return new Date(a.requested_at) - new Date(b.requested_at);
    return new Date(b.requested_at) - new Date(a.requested_at);
  });

  const newCount = leads.filter(l => l.request_status === 'new').length;
  const urgentCount = leads.filter(l => {
    if (l.request_status === 'closed') return false;
    const hours = (Date.now() - new Date(l.requested_at).getTime()) / 3600000;
    return hours >= 48 && l.request_status !== 'contacted';
  }).length;

  return (
    <div className="space-y-4" data-testid="leads-tab">
      {/* Urgent banner */}
      {urgentCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-red-600" />
          <p className="text-sm text-red-700 font-medium">{urgentCount} lead(s) waiting over 48 hours — respond to keep conversion high</p>
        </div>
      )}

      {/* Status summary */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {['new', 'providers_matched', 'reviewed', 'matched', 'contacted', 'closed'].map(s => {
          const badge = STATUS_BADGE[s];
          const isActive = statusFilter === s;
          return (
            <div key={s} className={`bg-white rounded-lg border p-3 text-center cursor-pointer transition-colors ${isActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
              onClick={() => setStatusFilter(isActive ? '' : s)}
              data-testid={`status-card-${s}`}>
              <p className="text-2xl font-medium">{statusCounts[s] || 0}</p>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
            </div>
          );
        })}
      </div>

      {/* Filters + sort */}
      <div className="flex gap-2 flex-wrap items-center">
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          className="text-xs border border-border rounded-md px-2 py-1.5 bg-white" data-testid="lead-cat-filter">
          <option value="">All categories</option>
          {['heat_pumps', 'boilers', 'ventilation', 'glazing', 'solar', 'batteries'].map(c =>
            <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
          )}
        </select>
        <button onClick={() => setSortOldest(!sortOldest)}
          className="text-xs border border-border rounded-md px-2 py-1.5 bg-white hover:border-primary flex items-center gap-1"
          data-testid="sort-toggle">
          <Clock className="w-3 h-3" /> {sortOldest ? 'Oldest first' : 'Newest first'}
        </button>
        <span className="text-xs text-muted-foreground">{sorted.length} lead(s)</span>
        {newCount > 0 && <span className="text-xs text-blue-600 font-medium">{newCount} new</span>}
      </div>

      {/* Lead list */}
      {loading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading leads...</p>
      ) : sorted.length === 0 ? (
        <div className="bg-white rounded-xl border border-border p-8 text-center">
          <Users className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No quote requests yet. They will appear when users request quotes from the report.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map(lead => {
            const sb = STATUS_BADGE[lead.request_status] || STATUS_BADGE.new;
            const expanded = expandedId === lead.request_id;
            const age = leadAge(lead.requested_at);

            return (
              <div key={lead.request_id}
                className={`bg-white rounded-xl border shadow-sm overflow-hidden ${age.urgent && lead.request_status !== 'contacted' && lead.request_status !== 'closed' ? 'border-red-200' : lead.request_status === 'new' ? 'border-blue-200' : 'border-border'}`}
                data-testid={`lead-${lead.request_id}`}>

                {/* Lead header — tap to expand */}
                <div className="p-4 cursor-pointer active:bg-muted/20" onClick={() => setExpandedId(expanded ? null : lead.request_id)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm capitalize">{lead.category?.replace(/_/g, ' ')}</span>
                        <span className="text-xs text-muted-foreground">{lead.region || lead.country}</span>
                        {lead.name && <span className="text-xs text-muted-foreground">· {lead.name}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span>{lead.email || 'No email'}</span>
                        <span className={`flex items-center gap-0.5 ${age.urgent ? 'text-red-600 font-medium' : ''}`}>
                          <Clock className="w-3 h-3" /> {age.text}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {lead.matched_providers?.length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{lead.matched_providers.length} provider(s)</span>
                      )}
                      {lead.matched_installers?.length > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700">{lead.matched_installers.length} matched</span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sb.cls}`}>{sb.label}</span>
                      <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                </div>

                {expanded && (
                  <div className="border-t border-border">
                    {/* Lead details */}
                    <div className="p-4 bg-muted/30 space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-muted-foreground">Category:</span> <span className="capitalize font-medium">{lead.category?.replace(/_/g, ' ')}</span></div>
                        <div><span className="text-muted-foreground">Region:</span> <span className="font-medium">{lead.region || '—'}</span></div>
                        <div><span className="text-muted-foreground">Postcode:</span> {lead.postcode || '—'}</div>
                        <div><span className="text-muted-foreground">Requested:</span> {new Date(lead.requested_at).toLocaleString()}</div>
                        <div>
                          <span className="text-muted-foreground">Email:</span>{' '}
                          {lead.email ? (
                            <button onClick={() => copyText(lead.email)} className="text-primary hover:underline inline-flex items-center gap-0.5">
                              {lead.email} <Copy className="w-3 h-3" />
                            </button>
                          ) : '—'}
                        </div>
                        <div><span className="text-muted-foreground">Name:</span> {lead.name || '—'}</div>
                      </div>

                      {lead.admin_notes && (
                        <p className="text-xs bg-white rounded p-2 border border-border"><span className="font-semibold">Notes:</span> {lead.admin_notes}</p>
                      )}

                      <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                        placeholder="Admin notes..."
                        className="w-full text-xs border border-border rounded px-2 py-1.5 bg-white" />

                      {/* Quick action buttons */}
                      <div className="flex gap-2 flex-wrap">
                        {lead.request_status === 'new' && (
                          <button onClick={() => updateLead(lead.request_id, 'reviewed')} disabled={updating === lead.request_id}
                            className="px-3 py-2 text-xs bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1"
                            data-testid={`review-${lead.request_id}`}>
                            {updating === lead.request_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                            Mark Reviewed
                          </button>
                        )}
                        {['matched', 'reviewed'].includes(lead.request_status) && (
                          <button onClick={() => updateLead(lead.request_id, 'contacted')} disabled={updating === lead.request_id}
                            className="px-3 py-2 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                            data-testid={`contact-${lead.request_id}`}>
                            <Phone className="w-3 h-3" /> Mark Contacted
                          </button>
                        )}
                        {lead.request_status !== 'closed' && (
                          <button onClick={() => updateLead(lead.request_id, 'closed')} disabled={updating === lead.request_id}
                            className="px-3 py-2 text-xs bg-muted text-foreground rounded-lg hover:bg-muted/80 flex items-center gap-1">
                            <XCircle className="w-3 h-3" /> Close
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Provider matching panel (Phase 1) */}
                    {lead.request_status !== 'closed' && (
                      <ProviderMatchPanel lead={lead} token={token} onChanged={loadLeads} />
                    )}

                    {/* Installer matching panel */}
                    {lead.request_status !== 'closed' && (
                      <MatchPanel lead={lead} token={token} onMatched={loadLeads} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
